/**
 * Enharmonic spelling.
 *
 * ## The policy, in full
 *
 * The recogniser only ever produces pitch classes. Letters are assigned here, at
 * session level, against the current `KeyEstimate`:
 *
 * 1. Compute a **key centre** on the line of fifths. For a major key this is the
 *    tonic's own line-of-fifths position. For a minor key it is the position of the
 *    *relative major* (tonic + 3 semitones), because that is what the key signature
 *    actually is — A minor and C major share a centre of 0, so A minor prefers F♮
 *    over E♯ exactly as a musician would write it.
 * 2. Enumerate every spelling of the pitch class using accidentals in [-2, 2].
 * 3. Pick the one whose line-of-fifths position is **closest to the centre**. This is
 *    what makes a root 0–6 fifths sharpward of the centre spell sharp-side and a root
 *    flatward spell flat-side, with no special-casing.
 * 4. Ties break toward **fewer accidentals**.
 * 5. Remaining ties break toward the spelling whose **whole third-stack costs fewer
 *    accidentals** — this is the diminished-7th / augmented-6th tie-breaker. In C
 *    major, pitch class 6 is equidistant as F♯ (+6) and G♭ (-6), but F♯dim7 spells
 *    F♯–A–C–E♭ (2 accidentals) where G♭dim7 spells G♭–B𝄫–D𝄫–F𝄫 (7), so F♯ wins.
 *    An augmented sixth is not representable as a stack of thirds at all, so the same
 *    rule is what keeps a German sixth in C spelled A♭7 rather than G♯7.
 * 6. A spelling whose third-stack cannot be written with accidentals in [-2, 2] is
 *    **illegal** and is rejected before any of the above is consulted.
 * 7. The final tie-break is the sharp side, matching convention for the tritone.
 */

import {
  LETTERS_FROM_C,
  LETTER_LINE_OF_FIFTHS,
  LETTER_PITCH_CLASSES,
  type ChordDegree,
  type Letter,
  type PitchClass,
  type SpelledNote,
} from './types.js';

/** A key estimate, structurally. Declared here to keep `chord/` free of a session import. */
export interface KeyContext {
  tonicPc: PitchClass;
  mode: 'major' | 'minor';
}

/** Wrap any integer into 0..11. */
export function mod12(n: number): PitchClass {
  return (((n % 12) + 12) % 12) as PitchClass;
}

/** The sounding pitch class of a spelled note. */
export function spelledNoteToPc(note: SpelledNote): PitchClass {
  return mod12(LETTER_PITCH_CLASSES[note.letter] + note.accidental);
}

/** Position of a spelled note on the line of fifths, C = 0, each accidental worth 7. */
export function lineOfFifths(note: SpelledNote): number {
  return LETTER_LINE_OF_FIFTHS[note.letter] + 7 * note.accidental;
}

/**
 * Line-of-fifths position of the key signature of each major key.
 * Ambiguous signatures resolve to the conventional choice: pc 1 is D♭ (5 flats)
 * rather than C♯ (7 sharps); pc 6 is F♯ (6 sharps) rather than G♭ (6 flats).
 */
const MAJOR_KEY_CENTRES: Readonly<Record<PitchClass, number>> = Object.freeze({
  0: 0, // C
  1: -5, // D flat
  2: 2, // D
  3: -3, // E flat
  4: 4, // E
  5: -1, // F
  6: 6, // F sharp
  7: 1, // G
  8: -4, // A flat
  9: 3, // A
  10: -2, // B flat
  11: 5, // B
});

/**
 * The line-of-fifths centre that spelling decisions are measured against.
 * Minor keys use their relative major, i.e. their actual key signature.
 */
export function keyCenterLof(key: KeyContext): number {
  const signaturePc = key.mode === 'minor' ? mod12(key.tonicPc + 3) : key.tonicPc;
  // The one seam where the relative major's convention is the wrong one for its minor.
  // F♯ beats G♭ as a major key, but E♭ minor beats D♯ minor by a long way — and taking
  // the six sharps here spelled every chord of an E♭ minor session on D♯, the tonic
  // included. The estimator cannot tell the two apart, so the commoner name wins.
  if (key.mode === 'minor' && signaturePc === 6) return -6;
  return MAJOR_KEY_CENTRES[signaturePc];
}

const ACCIDENTALS: ReadonlyArray<SpelledNote['accidental']> = [-2, -1, 0, 1, 2];

/**
 * Every spelling of a pitch class using accidentals in [-2, 2], ordered by
 * ascending line-of-fifths position (flattest first) for determinism.
 */
export function enharmonicCandidates(pc: PitchClass): SpelledNote[] {
  const out: SpelledNote[] = [];
  for (const letter of LETTERS_FROM_C) {
    for (const accidental of ACCIDENTALS) {
      if (mod12(LETTER_PITCH_CLASSES[letter] + accidental) === pc) {
        out.push({ letter, accidental });
      }
    }
  }
  out.sort((a, b) => lineOfFifths(a) - lineOfFifths(b));
  return out;
}

/**
 * How much one accidental is worth in fifths when ranking spellings.
 *
 * Three is the smallest integer that fixes the case this exists for: from a C minor
 * centre (lof −3), E natural scores 7 + 0 and F♭ scores 5 + 3, so E wins. At two they
 * tie and the distance tie-break hands it back to F♭.
 *
 * This is a deliberate trade, not a complete solution. A weight this high can also make
 * a natural beat an accidental that a theorist would prefer — in a key centred six or
 * more fifths sharp, a raised seventh may come out as F rather than E♯. That is a mild
 * notational infelicity in a key this product's user rarely visits; "F♭ major" where
 * "E major" belongs is a glaring one in the spec's own worked example. Trading the rare
 * mild error for the common glaring one is the right way round.
 *
 * The mechanical speller is a fallback by design: where the model supplies note
 * spellings that validate against the pitch classes, those win, because it has the
 * harmonic context that decides these cases properly (see the LLM validation layer).
 *
 * Note also that `KeyContext` carries a *pitch class*, so C♯ major and D♭ major are the
 * same key here, as are F♯ and G♭. The key estimator infers keys from sounding pitches
 * and genuinely cannot tell them apart, and the flat-side reading is the more common
 * choice for most of those pairs.
 */
const ACCIDENTAL_WEIGHT = 3;

/**
 * Rank the spellings of `pc` against a key centre, best first.
 *
 * `stackCost(candidate)` is an optional hook that returns the total accidental cost of
 * building the full chord on that root, or `null` if the stack is not writable at all.
 * `respell()` supplies it; plain note naming does not.
 */
export function rankSpellings(
  pc: PitchClass,
  centerLof: number,
  stackCost?: (root: SpelledNote) => number | null,
): SpelledNote[] {
  const scored = enharmonicCandidates(pc).map((note) => {
    const cost = stackCost ? stackCost(note) : 0;
    const lofDistance = Math.abs(lineOfFifths(note) - centerLof);
    const accidentalCost = Math.abs(note.accidental);
    return {
      note,
      legal: cost !== null,
      stackCost: cost ?? Number.POSITIVE_INFINITY,
      lofDistance,
      accidentalCost,
      // Distance to the key centre and remoteness of the spelling itself, combined
      // rather than ranked one-after-the-other. Pure lexicographic ordering on distance
      // means the accidental count is only ever consulted on an exact tie, which lets a
      // nearer-but-absurd spelling win outright: from a C minor centre, F♭ sits 5 fifths
      // away and E natural 7, so E major came out as F♭–A♭–C♭ — a spelling with eight
      // flats that no one writes. Weighting accidentals lets a plainer spelling overcome
      // a small distance disadvantage, while still letting a genuinely remote key have
      // its accidentals: in C♯ major the third is still E♯, not F.
      spellingCost: lofDistance + ACCIDENTAL_WEIGHT * accidentalCost,
      lof: lineOfFifths(note),
    };
  });

  scored.sort(
    (a, b) =>
      Number(b.legal) - Number(a.legal) ||
      a.spellingCost - b.spellingCost ||
      // Fewer accidentals before nearer, on an exact tie. The two only disagree where a
      // double accidental is three fifths nearer than a plainer spelling, and the one
      // centre where that happens in practice is E♭ minor's: A and B𝄫 both score 9
      // there, and nobody has ever written the chord on the tritone as B𝄫.
      a.accidentalCost - b.accidentalCost ||
      a.lofDistance - b.lofDistance ||
      a.stackCost - b.stackCost ||
      // Final tie-break: the sharp side. This is what makes pitch class 6 spell F#
      // rather than Gb in C major, where both are exactly six fifths from the centre.
      b.lof - a.lof,
  );

  return scored.map((s) => s.note);
}

/** The single best spelling of a pitch class in a key context. */
export function spellPitchClass(pc: PitchClass, key: KeyContext): SpelledNote {
  const ranked = rankSpellings(pc, keyCenterLof(key));
  // `enharmonicCandidates` is never empty: every pitch class has at least one
  // natural-or-single-accidental spelling.
  return ranked[0] as SpelledNote;
}

/**
 * The letter that a chord degree must use, given the root letter.
 * Degrees are letter-steps, so this never depends on accidentals.
 */
export function degreeLetter(rootLetter: Letter, steps: number): Letter {
  const start = LETTERS_FROM_C.indexOf(rootLetter);
  return LETTERS_FROM_C[(start + steps) % 7] as Letter;
}

/**
 * Spell a target pitch class on a required letter. Returns `null` when doing so would
 * need more than a double accidental, which is what "the third-stack is illegal" means.
 */
export function spellOnLetter(letter: Letter, targetPc: PitchClass): SpelledNote | null {
  let delta = mod12(targetPc - LETTER_PITCH_CLASSES[letter]) as number;
  if (delta > 6) delta -= 12;
  if (delta < -2 || delta > 2) return null;
  return { letter, accidental: delta as SpelledNote['accidental'] };
}

const UNICODE_ACCIDENTALS: Readonly<Record<SpelledNote['accidental'], string>> = Object.freeze({
  [-2]: '\u{1D12B}', // 𝄫
  [-1]: '♭', // ♭
  [0]: '',
  [1]: '♯', // ♯
  [2]: '\u{1D12A}', // 𝄪
});

const ASCII_ACCIDENTALS: Readonly<Record<SpelledNote['accidental'], string>> = Object.freeze({
  [-2]: 'bb',
  [-1]: 'b',
  [0]: '',
  [1]: '#',
  [2]: '##',
});

/** Render a spelled note, e.g. `{ letter: 'E', accidental: -1 }` → "E♭". */
export function noteName(note: SpelledNote, opts?: { ascii?: boolean }): string {
  const table = opts?.ascii ? ASCII_ACCIDENTALS : UNICODE_ACCIDENTALS;
  return note.letter + table[note.accidental];
}

/**
 * Fold every accidental glyph we might receive — Unicode musical symbols, the
 * ASCII stand-ins, and the naturals sign — down to `#`, `b` and nothing.
 * Exported because the chord parser needs exactly the same normalisation.
 */
export function normalizeAccidentalGlyphs(input: string): string {
  return input
    .replace(/\u{1D12B}/gu, 'bb')
    .replace(/\u{1D12A}/gu, '##')
    .replace(/♭/g, 'b')
    .replace(/♯/g, '#')
    .replace(/♮/g, '')
    .replace(/𝄫/g, 'bb')
    .replace(/𝄪/g, '##');
}

/** Parse a single note name such as "E♭", "Eb", "F##", "Gx". Returns null if it is not one. */
export function parseNoteName(input: string): SpelledNote | null {
  const text = normalizeAccidentalGlyphs(input.trim());
  const match = /^([A-Ga-g])(bb|b|##|#|x|)$/.exec(text);
  if (!match) return null;
  const letter = (match[1] as string).toUpperCase() as Letter;
  const suffix = match[2] as string;
  const accidental: SpelledNote['accidental'] =
    suffix === 'bb' ? -2 : suffix === 'b' ? -1 : suffix === '#' ? 1 : suffix === '' ? 0 : 2;
  return { letter, accidental };
}

/**
 * Map a list of note-name strings to pitch classes, e.g. for validating the
 * `noteSpellings` an LLM returned against the pitch classes of the chord it claims.
 * Returns `null` if any entry is not a note name.
 */
export function pcsFromNoteNames(names: readonly string[]): PitchClass[] | null {
  const out: PitchClass[] = [];
  for (const name of names) {
    const note = parseNoteName(name);
    if (!note) return null;
    out.push(spelledNoteToPc(note));
  }
  return out;
}

/** Convenience re-export shape used by `canonical.ts` when walking a stack of thirds. */
export type DegreeSpelling = { degree: ChordDegree; note: SpelledNote; pc: PitchClass };
